import { createTableDefaultColumn } from '@common/tableColumn.services';
import {
  IProjectDesignModificationOptionForDesignerTransmittal,
  IProjectDesignModificationWithLink,
} from '@designModification/designModification.models';
import { Accordion, FormFieldMethods, FormSelect, IOption, PaginatedTable } from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { IUpsertProjectTransmittalDesignModification } from '@transmittal/transmittal.models';
import * as React from 'react';
import { ETransmittalStatus } from '@transmittal/transmittal.constants';
import { ETransmittalDetailsFormKeys } from './transmittalDetailPage.constants';

interface ITransmittalDetailChangeForT3Props {
  formFieldMethods: FormFieldMethods;
  isReadOnly: boolean;
  designModificationOptions?: IProjectDesignModificationOptionForDesignerTransmittal[];
  transmittalId?: number;
  transmittalStatus?: ETransmittalStatus;
  designModifications?: IProjectDesignModificationWithLink[];
}
export const TransmittalDetailChangeForT3: React.FunctionComponent<ITransmittalDetailChangeForT3Props> = ({
  formFieldMethods,
  isReadOnly,
  designModificationOptions,
  transmittalId,
  transmittalStatus,
  designModifications,
}) => {
  const { navigate } = useNavigate();
  const designChangeItems: IUpsertProjectTransmittalDesignModification[] =
    formFieldMethods.watch(ETransmittalDetailsFormKeys.CHANGE_T3) ?? [];

  const columns = React.useMemo(
    () => [
      createTableDefaultColumn<IProjectDesignModificationOptionForDesignerTransmittal>(
        'designChangeNo',
        'Design Change No.',
        ({ value, item }) => {
          navigate(routes.t3.view.path, { id: item.id });
        },
        ({ item }) => {
          return 'gcl-fl-md';
        },
        ({ item }) => {
          return { cursor: 'pointer' };
        },
      ),
      {
        Header: 'Location',
        accessor: 'location',
        disableResizing: false,
        disableSortBy: false,
      },
      {
        Header: 'Element of Works',
        accessor: 'elementOfWorks',
        disableResizing: false,
        disableSortBy: false,
      },
    ],
    [],
  );

  const optionsForFormSelect = React.useMemo(() => {
    return (designModificationOptions ?? []).map((item) => {
      return { key: item.id.toString(), text: item.designChangeNo ?? '', data: item, ...item };
    });
  }, [JSON.stringify(designModificationOptions)]);

  const selected = React.useMemo(() => {
    if (transmittalStatus === ETransmittalStatus.APPROVED) {
      return designModifications?.map((d) => {
        const item: IProjectDesignModificationOptionForDesignerTransmittal = {
          projectTransmittalDesignModificationRowVersion: d.projectTransmittalDesignModificationRowVersion,
          projectTransmittalDesignModificationLinkId: d.projectTransmittalDesignModificationLinkId,
          id: d.id,
          projectMasterScheduleId: d.projectMasterScheduleId,
          designChangeNo: d.designChangeNo,
          location: d.location,
          elementOfWorks: d.elementOfWorks,
          status: d.status,
        };
        return item;
      });
    }
    const selectedT3Ids = (designChangeItems ?? [])
      .map((i) => i.projectDesignModificationId)
      .filter((i): i is number => !!i);

    const selectedItems = designModificationOptions?.filter((option) => option.id && selectedT3Ids.includes(option.id));
    return selectedItems;
  }, [designChangeItems, transmittalStatus, JSON.stringify(designModifications)]);

  const onRenderListAccordionBody = () => {
    return (
      <>
        {!isReadOnly && (
          <FormSelect
            displayFieldName="designChangeNo"
            placeholder="Select T3"
            multiple
            formFieldMethods={formFieldMethods}
            options={optionsForFormSelect}
            id={ETransmittalDetailsFormKeys.CHANGE_T3}
            transform={{
              input: (
                value:
                  | IOption<IProjectDesignModificationOptionForDesignerTransmittal>
                  | IOption<IProjectDesignModificationOptionForDesignerTransmittal>[]
                  | undefined
                  | null,
              ) => {
                if (value === undefined || value === null) return undefined;
                return value;
              },
              output: (value: IOption<IProjectDesignModificationOptionForDesignerTransmittal>[]) => {
                if (value === undefined || value === null) return undefined;
                return (value as IOption<IProjectDesignModificationOptionForDesignerTransmittal>[]).map((v) => {
                  const temp = { ...v, ...v.data } as IProjectDesignModificationOptionForDesignerTransmittal;
                  if (temp.id) {
                    return {
                      transmittalId,
                      projectDesignModificationId: temp.id,
                      designChangeNo: temp.designChangeNo,
                    };
                  }
                  return temp;
                });
              },
            }}
          />
        )}
        <PaginatedTable columns={columns} items={selected ?? []} disablePagination allowSelectRow={false} />
      </>
    );
  };
  return (
    <div>
      <Accordion
        allowClickHeaderToExpand
        content={{
          id: 'Change for T3',
          onRenderBody: onRenderListAccordionBody,
          onRenderHeader: () => {
            return <span>Change for T3</span>;
          },
        }}
      />
    </div>
  );
};
